import { useEffect, useState } from "react";
import { TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";

interface Props {
  investigation: { id: string; name: string; fileCount?: number } | null; // null = closed
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

// Confirmation for deleting an investigation. Same shell as the other
// Investigations modals. Deletion is a soft delete on the server: the
// record leaves every list, but files, audio, and the AI record stay for
// chain of custody, which is what the "Kept" row explains.
const DeleteInvestigationModal: React.FC<Props> = ({ investigation, onClose, onConfirm }) => {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!investigation) return;
    setBusy(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !busy && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [investigation, busy, onClose]);

  if (!investigation) return null;

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <dialog open className="modal modal-open">
      <div className="modal-box max-w-md p-0 overflow-hidden rounded-2xl">
        <div className="flex items-start gap-3 px-6 pt-6 pb-4">
          <div className="size-10 shrink-0 rounded-xl bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 flex items-center justify-center">
            <TrashIcon className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-base-content leading-tight">Delete this investigation?</h3>
            <p className="text-sm text-base-content/60 mt-0.5 truncate" title={investigation.name}>
              {investigation.name}
              {investigation.fileCount ? ` · ${investigation.fileCount} file${investigation.fileCount === 1 ? "" : "s"}` : ""}
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={busy} className="p-1 rounded-md text-base-content/50 hover:bg-base-200 hover:text-base-content" title="Close">
            <XMarkIcon className="size-5" />
          </button>
        </div>
        <ul className="mx-6 mb-5 rounded-xl border border-base-300 bg-base-200/50 divide-y divide-base-300 text-sm">
          <li className="px-4 py-2.5 flex gap-3">
            <span className="w-20 shrink-0 text-xs font-semibold uppercase tracking-wider text-base-content/50 pt-0.5">Removed</span>
            <span className="text-base-content/80">The investigation disappears from the list for you and every member. Uploads and chat stop.</span>
          </li>
          <li className="px-4 py-2.5 flex gap-3">
            <span className="w-20 shrink-0 text-xs font-semibold uppercase tracking-wider text-base-content/50 pt-0.5">Kept</span>
            <span className="text-base-content/80">Audio, files, transcripts, leads, and the AI record are retained for chain of custody and discovery. This action is logged.</span>
          </li>
        </ul>
        <div className="px-6 pb-6 flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={confirm} disabled={busy}>
            {busy ? "Deleting…" : "Delete investigation"}
          </Button>
        </div>
      </div>
      <button type="button" className="modal-backdrop" onClick={() => !busy && onClose()} aria-label="Close" />
    </dialog>
  );
};

export default DeleteInvestigationModal;
